import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogIcon,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { CircleNotch, XCircle } from "@phosphor-icons/react";
import { formatDateWithOptionsBs } from "@/lib/format";
import { formatTimeBs } from "@/lib/timezone";
import type { CalendarSession } from "./types";

interface CancelSessionDialogProps {
  session: CalendarSession;
  onClose: () => void;
  onConfirm: (data: { reason?: string; applyFee?: boolean }) => void;
  isPending: boolean;
  /** Show the admin-only "apply fee" checkbox. */
  showFeeOption?: boolean;
  /** Plaćeni termin: šalje se zahtjev adminu umjesto trenutnog otkaza. */
  requiresApproval?: boolean;
}

export function CancelSessionDialog({ session, onClose, onConfirm, isPending, showFeeOption = false, requiresApproval = false }: CancelSessionDialogProps) {
  const [reason, setReason] = useState("");
  const [applyFee, setApplyFee] = useState(false);
  const tooShort = requiresApproval && reason.trim().length < 5;

  const dateFormatted = formatDateWithOptionsBs(session.scheduledAt, {
    weekday: "short", day: "numeric", month: "long", timeZone: "Europe/Sarajevo",
  });

  return (
    <Dialog open onClose={onClose} className="sm:max-w-md">
      <DialogHeader onClose={onClose}>
        <div className="flex items-center gap-3">
          <DialogIcon tone="destructive">
            <XCircle className="size-5" />
          </DialogIcon>
          <div>
            <DialogTitle>{requiresApproval ? "Zatraži otkazivanje" : "Otkaži termin"}</DialogTitle>
            <DialogDescription className="mt-0 text-xs">
              {requiresApproval
                ? "Zahtjev ide adminu na odobrenje jer nosi povrat novca klijentu"
                : "Klijent će biti obaviješten putem emaila"}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <DialogBody className="space-y-4">
        <div className="rounded-xl bg-muted/30 p-3 text-sm">
          <p className="font-medium text-foreground">{session.clientName}</p>
          <p className="text-muted-foreground">{dateFormatted} · {formatTimeBs(session.scheduledAt)}</p>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {requiresApproval ? "Razlog (obavezno)" : "Razlog (opcionalno)"}
          </label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Npr. terapeut spriječen, klijent zamolio za otkazivanje…"
          />
        </div>

        {showFeeOption && (
          <label className="flex items-center gap-2 text-sm text-foreground">
            <Checkbox checked={applyFee} onCheckedChange={(v) => setApplyFee(v === true)} />
            Primijeni naknadu klijentu (standardni procenat)
          </label>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isPending}>
          Nazad
        </Button>
        <Button
          variant={requiresApproval ? "default" : "destructive"}
          onClick={() => onConfirm({ reason: reason.trim() || undefined, applyFee: showFeeOption ? applyFee : undefined })}
          disabled={isPending || tooShort}
        >
          {isPending ? <CircleNotch className="size-4 animate-spin" /> : <XCircle className="size-4" />}
          {requiresApproval ? "Pošalji zahtjev" : "Otkaži termin"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
